import React from 'react';
import { Typography } from '../common/Typography';
import Loading from '../vulcan-core/Loading';
import LoadMore from '../common/LoadMore';
import CommentsNode, { COMMENT_DRAFT_TREE_OPTIONS } from './CommentsNode';
import { AnalyticsContext } from '@/lib/analyticsEvents';
import { useCommentLinkState } from './CommentsItem/useCommentLink';
import { gql } from '@/lib/generated/gql-codegen';
import { useQuery } from "@/lib/crud/useQuery";
import { useQueryWithLoadMore } from '../hooks/useQueryWithLoadMore';
import SectionTitle from '../common/SectionTitle';
import { DraftCommentsQuery } from './queries';
import { defineStyles } from '@/components/hooks/defineStyles';
import { useStyles } from '@/components/hooks/useStyles';

const LinkedDraftCommentQuery = gql(`
  query LinkedDraftCommentQuery($documentId: String!) {
    comment(selector: { _id: $documentId }) {
      result {
        ...DraftComments
      }
    }
  }
`);

const styles = defineStyles('CommentsDraftList', (theme: ThemeType) => ({
  heading: {
    display: 'flex',
    marginBottom: 8,
    fontSize: 16,
    fontWeight: 600,
    fontFamily: theme.palette.fonts.sansSerifStack,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 500,
  },
  noResults: {
    marginLeft: 8,
    color: theme.palette.text.dim4,
  }
}), { stylePriority: 1 });

const CommentsDraftList = ({userId, postId, initialLimit, itemsPerPage, showTotal, silentIfEmpty, sectionTitleStyle, title, quickTakesOnly}: {
  userId: string,
  postId?: string,
  initialLimit?: number,
  itemsPerPage?: number,
  showTotal?: boolean,
  silentIfEmpty?: boolean,
  sectionTitleStyle?: boolean,
  title?: string,
  quickTakesOnly?: boolean,
}) => {
  const classes = useStyles(styles);
  const { linkedCommentId } = useCommentLinkState();
  const sectionTitle = title ?? (quickTakesOnly ? "Quick Take drafts" : "Draft comments");

  // Usually, there will be no linked comment (`?commentId=...` in the url), and the rawResults below
  // will be displayed directly. If there is a linked comment, bump it to the top of the list so
  // we know we can scroll to it.
  const { data: linkedCommentData, loading: linkedCommentLoading } = useQuery(LinkedDraftCommentQuery, {
    variables: {
      documentId: linkedCommentId,
    },
    skip: !linkedCommentId,
  });

  const linkedComment = linkedCommentData?.comment?.result;

  const { data: draftCommentsData, loading: draftCommentsLoading, loadMoreProps } = useQueryWithLoadMore(DraftCommentsQuery, {
    variables: {
      selector: {
        draftComments: {
          userId,
          postId,
          shortform: quickTakesOnly ? true : undefined,
          drafts: "drafts-only",  
        },
      },
      limit: initialLimit,
      enableTotal: true,
    },
    itemsPerPage,
  });

  const rawResults = draftCommentsData?.comments?.results;
  const totalCount = draftCommentsData?.comments?.totalCount ?? undefined;

  // Move the linked comment up to the top if given
  const results = ([linkedComment, ...(rawResults ?? [])]
    .filter(v => !!v)
    .filter(v => v.draft))
    .filter(comment => !quickTakesOnly || comment.shortform)
    .reduce<DraftComments[]>((acc, comment) => {
      if (!acc.some(existingComment => existingComment._id === comment?._id)) {
        acc.push(comment);
      }
      return acc;
    }, []);
  const loading = draftCommentsLoading || (!linkedComment && linkedCommentLoading);
  const count = results.length;

  if (loading && !results?.length) {
    return !silentIfEmpty ? <Loading/> : null;
  }

  const showLoadMore = !loading && (count === undefined || totalCount === undefined || count < totalCount)

  return <AnalyticsContext pageElementContext="commentsDraftList">
    {(!silentIfEmpty || !!results?.length) && (sectionTitleStyle
      ? <SectionTitle titleClassName={classes.sectionTitle} title={sectionTitle}/>
      : <Typography variant="headline" className={classes.heading}>{sectionTitle}</Typography>
    )}
    {(!silentIfEmpty && !loading && results?.length === 0) && (
      <Typography variant="body2" className={classes.noResults}>
        No comments to display.
      </Typography>
    )}
    {!!results && results.map((comment) => (
      <CommentsNode
        comment={comment}
        key={comment._id}
        // Don't auto-scroll to this comment if it probably appears as a reply elsewhere in the page, prefer showing it properly in context
        noAutoScroll={!!(postId && comment.parentCommentId)}
        treeOptions={{
          ...COMMENT_DRAFT_TREE_OPTIONS,
          singleLinePostTitle: !postId && !quickTakesOnly,
          showPostTitle: !postId && !quickTakesOnly,
          post: comment.post || undefined,
          showEditInContext: !postId && !quickTakesOnly,
          redirectAfterEditSubmit: !!quickTakesOnly,
          // noAutoScroll isn't sufficient to prevent scrolling by itself, probably because there's multiple things that can cause scrolls
          // and clicking on a link with a hash in it might be sufficient by itself.
          noDOMId: true,
        }}
      />
    ))}
    {loading && <Loading/>}
    {showLoadMore && <LoadMore {...{
      ...loadMoreProps,
      count,
      totalCount: showTotal ? totalCount : undefined,
    }} />}
  </AnalyticsContext>;
}

export default CommentsDraftList;
